import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { HorizontalBar } from 'react-chartjs-2';
import {
  MdLocationCity,
  MdMap,
  MdRefresh,
  MdStorage,
  MdTimeline
} from 'react-icons/md';

import Api from 'services/Api';
import {
  chartColors,
  KPI,
  getDashboardCardSurface,
  getDashboardShellStyles,
  getDashboardTabsStyle,
  getChartTextColor
} from 'constants/dashboardColors';
import '../PainelExecutivo.css';

const EMPTY_METRICS_BLOCK = {
  totals: {
    totalMeasures: 0,
    measuresToday: 0,
    measuresLast7Days: 0,
    measuresLast30Days: 0,
    activeEquipments: 0,
    activeCities: 0,
    activeStates: 0,
    totalCurves: 0,
    gpsCoverage: 0
  },
  averages: {
    humidity: null,
    temperature: null,
    peso: null,
    freqc: null,
    freqv: null
  },
  maximums: {
    humidity: null,
    temperature: null,
    peso: null,
    freqc: null,
    freqv: null
  },
  minimums: {
    humidity: null,
    temperature: null,
    peso: null,
    freqc: null,
    freqv: null
  },
  topGrains: [],
  topStates: [],
  topCities: [],
  topEquipments: [],
  distributions: {
    humidityRanges: {},
    temperatureRanges: {}
  },
  activity: {
    mostActiveHour: null,
    leastActiveHour: null,
    mostActiveWeekDay: null
  },
  lastMeasurement: null
};

const EMPTY_EXECUTIVE_DASHBOARD = {
  generatedAt: null,
  topsPeriod: 'last30Days',
  cached: false,
  cacheAgeSeconds: 0,
  dry: EMPTY_METRICS_BLOCK,
  bench: EMPTY_METRICS_BLOCK
};

const humidityRangeConfig = [
  ['below10', '< 10%'],
  ['between10And12', '10% a 12%'],
  ['between12And14', '12% a 14%'],
  ['between14And16', '14% a 16%'],
  ['above16', '≥ 16%']
];

const temperatureRangeConfig = [
  ['below10', '< 10°C'],
  ['between10And20', '10°C a 20°C'],
  ['between20And30', '20°C a 30°C'],
  ['above30', '≥ 30°C']
];

const statFields = [
  { key: 'humidity', label: 'Umidade', unit: '%' },
  { key: 'temperature', label: 'Temperatura', unit: '°C' },
  { key: 'peso', label: 'Peso', unit: '' }
];

const weekDayLabels = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

const normalizeMetricsBlock = (block = {}) => ({
  totals: {
    ...EMPTY_METRICS_BLOCK.totals,
    ...(block.totals || {})
  },
  averages: {
    ...EMPTY_METRICS_BLOCK.averages,
    ...(block.averages || {})
  },
  maximums: {
    ...EMPTY_METRICS_BLOCK.maximums,
    ...(block.maximums || {})
  },
  minimums: {
    ...EMPTY_METRICS_BLOCK.minimums,
    ...(block.minimums || {})
  },
  topGrains: Array.isArray(block.topGrains) ? block.topGrains : [],
  topStates: Array.isArray(block.topStates) ? block.topStates : [],
  topCities: Array.isArray(block.topCities) ? block.topCities : [],
  topEquipments: Array.isArray(block.topEquipments) ? block.topEquipments : [],
  distributions: {
    humidityRanges: block.distributions?.humidityRanges || {},
    temperatureRanges: block.distributions?.temperatureRanges || {}
  },
  activity: {
    ...EMPTY_METRICS_BLOCK.activity,
    ...(block.activity || {})
  },
  lastMeasurement: block.lastMeasurement || null
});

const normalizeExecutiveDashboard = (data = {}) => ({
  generatedAt: data.generatedAt || null,
  topsPeriod: data.topsPeriod || 'last30Days',
  cached: Boolean(data.cached),
  cacheAgeSeconds: Number(data.cacheAgeSeconds || 0),
  dry: normalizeMetricsBlock(data.dry),
  bench: normalizeMetricsBlock(data.bench)
});

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const formatDecimal = (value, unit = '') => {
  if (value === null || value === undefined || value === '') return '--';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '--';

  return `${numeric.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}${unit}`;
};

const formatPercent = (value) => `${formatDecimal(value)}%`;

const formatGeneratedAt = (value) => {
  if (!value) return 'Sem data de geração';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem data de geração';

  return date.toLocaleString('pt-BR');
};

const formatDateTime = (value) => {
  if (!value) return '--';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';

  return date.toLocaleString('pt-BR');
};

const formatHour = (hour) => {
  if (hour === null || hour === undefined || hour === '') return '--';
  return `${String(hour).padStart(2, '0')}h`;
};

const formatWeekDay = (day) => {
  const numeric = Number(day);
  if (Number.isNaN(numeric) || numeric < 0 || numeric > 6) return '--';
  return weekDayLabels[numeric] || '--';
};

const rangesToSeries = (ranges = {}, config = []) => (
  config
    .map(([key, label]) => ({
      label,
      value: Number(ranges?.[key] || 0)
    }))
    .filter((item) => item.value > 0)
);

const topListToSeries = (items = [], labelKey) => (
  (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      label: item?.[labelKey] || item?.value || `Item ${index + 1}`,
      value: Number(item?.count || 0)
    }))
    .filter((item) => item.value > 0)
);

const createGradient = (ctx, colors, darkMode) => {
  const gradient = ctx.createLinearGradient(0, 0, 300, 0);
  gradient.addColorStop(0, darkMode ? colors.dark : colors.light);
  gradient.addColorStop(1, darkMode ? `${colors.dark}99` : `${colors.light}99`);
  return gradient;
};

const getChartOptions = (darkMode, datasetLabel = 'Quantidade') => ({
  animation: {
    onComplete: function () {
      const chart = this.chart;
      const ctx = chart.ctx;

      ctx.save();
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      chart.config.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.controller.getDatasetMeta(datasetIndex);
        const chartRight = chart.chartArea?.right || chart.width;
        const chartLeft = chart.chartArea?.left || 0;

        meta.data.forEach((bar, index) => {
          const value = formatNumber(dataset.data[index]);
          const model = bar._model;
          const barWidth = Math.abs(model.x - model.base);
          const textWidth = ctx.measureText(value).width;
          const fitsInside = barWidth > textWidth + 18;
          const x = fitsInside
            ? Math.min(Math.max(model.x - 10, chartLeft + textWidth + 4), chartRight - 4)
            : Math.min(model.x + textWidth + 10, chartRight - 4);

          ctx.fillStyle = fitsInside ? '#ffffff' : darkMode ? '#f8f9fa' : '#1c4220';
          ctx.fillText(value, x, model.y);
        });
      });

      ctx.restore();
    }
  },
  scales: {
    yAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(darkMode),
        fontStyle: 'bold',
        precision: 0
      },
      gridLines: { display: false },
      maxBarThickness: 30
    }],
    xAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(darkMode),
        precision: 0
      },
      gridLines: {
        color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        zeroLineColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    }]
  },
  legend: { display: false },
  maintainAspectRatio: false,
  responsive: true,
  tooltips: {
    backgroundColor: darkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
    titleFontColor: darkMode ? '#f8f9fa' : '#333',
    bodyFontColor: darkMode ? '#adb5bd' : '#666',
    callbacks: {
      label: (item) => `${datasetLabel}: ${item.value}`
    }
  }
});

const MetricCard = ({ icon, title, value, subtitle, accent, darkMode, isTextValue = false }) => (
  <Col xs={12} sm={6} lg={3}>
    <div
      className="h-100"
      style={{
        borderRadius: '18px',
        padding: '18px',
        ...getDashboardCardSurface(),
        borderTop: `4px solid ${accent}`
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p className="text-uppercase mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', color: darkMode ? '#adb5bd' : '#6c757d' }}>
            {title}
          </p>
          <h2 className="mb-0 fw-bold" style={{ color: darkMode ? '#f8f9fa' : '#1c4220', fontSize: isTextValue ? '1.35rem' : undefined }}>
            {value}
          </h2>
        </div>
        <div className="d-inline-flex align-items-center justify-content-center" style={{ width: '46px', height: '46px', borderRadius: '14px', background: `${accent}22`, color: accent }}>
          {icon}
        </div>
      </div>
      {subtitle && <small style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>{subtitle}</small>}
    </div>
  </Col>
);

const ChartCard = ({ title, series, colorKey, darkMode, lg = 6, height = 280, datasetLabel }) => {
  const colors = chartColors[colorKey] || chartColors.green;
  const options = getChartOptions(darkMode, datasetLabel);

  return (
    <Col xs={12} lg={lg}>
      <div
        className="h-100"
        style={{
          padding: '18px',
          borderRadius: '18px',
          ...getDashboardCardSurface(),
          borderTop: `4px solid ${darkMode ? colors.dark : colors.light}`
        }}
      >
        <h5 className="fw-bold mb-3" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>{title}</h5>
        <div style={{ height, position: 'relative' }}>
          {series.length ? (
            <HorizontalBar
              data={(canvas) => {
                const ctx = canvas.getContext('2d');
                return {
                  labels: series.map((item) => item.label),
                  datasets: [{
                    label: datasetLabel,
                    data: series.map((item) => item.value),
                    backgroundColor: createGradient(ctx, colors, darkMode),
                    borderColor: darkMode ? colors.dark : colors.light,
                    borderWidth: 1
                  }]
                };
              }}
              options={options}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">Sem dados para exibir</div>
          )}
        </div>
      </div>
    </Col>
  );
};

const RankingCard = ({ title, series, darkMode, accent = '#8fad15' }) => {
  const maxValue = Math.max(...series.map((item) => item.value), 0);

  return (
    <Col xs={12} md={6} xl={3}>
      <div
        className="h-100"
        style={{
          padding: '18px',
          borderRadius: '18px',
          ...getDashboardCardSurface()
        }}
      >
        <h5 className="fw-bold mb-3" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>{title}</h5>
        {series.length ? series.slice(0, 10).map((item) => (
          <div key={`${title}-${item.label}`} className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-truncate me-2" title={item.label} style={{ color: darkMode ? '#f8f9fa' : '#1c4220', fontWeight: 600 }}>
                {item.label}
              </span>
              <span style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>{formatNumber(item.value)}</span>
            </div>
            <div style={{ height: '8px', background: darkMode ? 'rgba(255, 255, 255, 0.08)' : '#edf2ee', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ width: `${maxValue ? Math.round((item.value / maxValue) * 100) : 0}%`, height: '100%', background: accent, borderRadius: '999px' }} />
            </div>
          </div>
        )) : (
          <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '160px' }}>Sem dados para exibir</div>
        )}
      </div>
    </Col>
  );
};

const StatsPanel = ({ averages, maximums, minimums, darkMode, styles }) => (
  <Col xs={12}>
    <div style={{ ...styles.panel, padding: '18px' }}>
      <h5 className="fw-bold mb-3" style={styles.titleText}>Estatísticas — últimos 30 dias</h5>
      <div className="table-responsive">
        <table className="table table-sm mb-0" style={{ color: darkMode ? '#f8f9fa' : '#1c4220' }}>
          <thead>
            <tr style={{ color: darkMode ? '#adb5bd' : '#6c757d' }}>
              <th>Indicador</th>
              <th>Média</th>
              <th>Máximo</th>
              <th>Mínimo</th>
            </tr>
          </thead>
          <tbody>
            {statFields.map(({ key, label, unit }) => (
              <tr key={key}>
                <td className="fw-semibold">{label}</td>
                <td>{formatDecimal(averages?.[key], unit)}</td>
                <td>{formatDecimal(maximums?.[key], unit)}</td>
                <td>{formatDecimal(minimums?.[key], unit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </Col>
);

const LastMeasurementCard = ({ measurement, darkMode, styles }) => (
  <Col xs={12} lg={4}>
    <div className="h-100" style={{ ...styles.panel, padding: '18px' }}>
      <h5 className="fw-bold mb-3" style={styles.titleText}>Última medida</h5>
      {measurement ? (
        <>
          <p className="mb-1" style={styles.mutedText}>
            <strong style={styles.titleText}>{measurement.serialnumber || '--'}</strong>
          </p>
          <p className="mb-1 small" style={styles.mutedText}>{measurement.grain || 'Grão não informado'}</p>
          <p className="mb-1 small" style={styles.mutedText}>
            Umidade: {measurement.humidity ?? '--'}% · Temp.: {measurement.temperature ?? '--'}°C
          </p>
          <p className="mb-1 small" style={styles.mutedText}>
            {[measurement.city, measurement.state].filter(Boolean).join(' — ') || 'Local não informado'}
          </p>
          <p className="mb-0 small" style={styles.mutedText}>{formatDateTime(measurement.createdAt)}</p>
        </>
      ) : (
        <p className="mb-0 text-muted">Nenhuma medida registrada</p>
      )}
    </div>
  </Col>
);

const ActivityPanel = ({ activity, darkMode, styles }) => (
  <Col xs={12} lg={8}>
    <div className="h-100" style={{ ...styles.panel, padding: '18px' }}>
      <h5 className="fw-bold mb-3" style={styles.titleText}>Padrão de atividade — últimos 30 dias</h5>
      <Row className="g-3">
        <Col sm={4}>
          <p className="mb-1 small text-uppercase" style={styles.mutedText}>Hora de pico</p>
          <p className="mb-0 fw-bold" style={styles.titleText}>{formatHour(activity.mostActiveHour)}</p>
        </Col>
        <Col sm={4}>
          <p className="mb-1 small text-uppercase" style={styles.mutedText}>Hora de menor volume</p>
          <p className="mb-0 fw-bold" style={styles.titleText}>{formatHour(activity.leastActiveHour)}</p>
        </Col>
        <Col sm={4}>
          <p className="mb-1 small text-uppercase" style={styles.mutedText}>Dia mais ativo</p>
          <p className="mb-0 fw-bold" style={styles.titleText}>{formatWeekDay(activity.mostActiveWeekDay)}</p>
        </Col>
      </Row>
    </div>
  </Col>
);

const MetricsSection = ({ title, badge, metrics, topsPeriod, darkMode, styles }) => {
  const { totals, averages, maximums, minimums, distributions, activity, lastMeasurement } = metrics;
  const humiditySeries = rangesToSeries(distributions.humidityRanges, humidityRangeConfig);
  const temperatureSeries = rangesToSeries(distributions.temperatureRanges, temperatureRangeConfig);
  const topsPeriodLabel = topsPeriod === 'last30Days' ? 'últimos 30 dias' : topsPeriod;

  if (!totals.totalMeasures) {
    return (
      <div style={{ ...styles.panel, padding: '28px' }}>
        <Badge className="mb-2" style={{ background: '#8fad15', color: '#fff' }}>{badge}</Badge>
        <h4 className="fw-bold mb-2" style={styles.titleText}>{title}</h4>
        <p className="mb-0" style={styles.mutedText}>Nenhuma medida encontrada para este tipo de equipamento.</p>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Badge style={{ background: '#8fad15', color: '#fff' }}>{badge}</Badge>
        <span style={styles.mutedText}>Rankings e distribuições: {topsPeriodLabel}</span>
        <span style={styles.mutedText}>· Curvas cadastradas: {formatNumber(totals.totalCurves)}</span>
      </div>

      <Row className="g-3 mb-3">
        <MetricCard icon={<MdTimeline size={28} />} title="Hoje" value={formatNumber(totals.measuresToday)} subtitle="Medidas no dia" accent={KPI.primary} darkMode={darkMode} />
        <MetricCard icon={<MdTimeline size={28} />} title="Últimos 7 Dias" value={formatNumber(totals.measuresLast7Days)} subtitle="Janela semanal" accent={KPI.secondary} darkMode={darkMode} />
        <MetricCard icon={<MdTimeline size={28} />} title="Últimos 30 Dias" value={formatNumber(totals.measuresLast30Days)} subtitle="Janela mensal" accent={KPI.tertiary} darkMode={darkMode} />
        <MetricCard icon={<MdStorage size={28} />} title="Total Histórico" value={formatNumber(totals.totalMeasures)} subtitle="Base completa" accent={KPI.quaternary} darkMode={darkMode} />
      </Row>

      <Row className="g-3 mb-4">
        <MetricCard icon={<MdStorage size={28} />} title="Equipamentos Ativos" value={formatNumber(totals.activeEquipments)} subtitle="Com última medida" accent={KPI.secondary} darkMode={darkMode} />
        <MetricCard icon={<MdMap size={28} />} title="Estados Ativos" value={formatNumber(totals.activeStates)} subtitle="Últimas medições" accent={KPI.quaternary} darkMode={darkMode} />
        <MetricCard icon={<MdLocationCity size={28} />} title="Cidades Ativas" value={formatNumber(totals.activeCities)} subtitle="Últimas medições" accent={KPI.tertiary} darkMode={darkMode} />
        <MetricCard icon={<MdMap size={28} />} title="Cobertura GPS" value={formatPercent(totals.gpsCoverage)} subtitle="Equipamentos com lat/lng" accent={KPI.primary} darkMode={darkMode} isTextValue />
      </Row>

      <Row className="g-4 mb-4">
        <LastMeasurementCard measurement={lastMeasurement} darkMode={darkMode} styles={styles} />
        <ActivityPanel activity={activity} darkMode={darkMode} styles={styles} />
      </Row>

      <Row className="g-4 mb-4">
        <StatsPanel averages={averages} maximums={maximums} minimums={minimums} darkMode={darkMode} styles={styles} />
      </Row>

      <Row className="g-4 mb-4">
        <ChartCard title="Distribuição de Umidade" series={humiditySeries} colorKey="teal" darkMode={darkMode} lg={6} datasetLabel="Medidas" />
        <ChartCard title="Distribuição de Temperatura" series={temperatureSeries} colorKey="orange" darkMode={darkMode} lg={6} datasetLabel="Medidas" />
      </Row>

      <Row className="g-4 mb-4">
        <RankingCard title="Top Grãos" series={topListToSeries(metrics.topGrains, 'grain')} darkMode={darkMode} accent={KPI.primary} />
        <RankingCard title="Top Estados" series={topListToSeries(metrics.topStates, 'state')} darkMode={darkMode} accent={KPI.secondary} />
        <RankingCard title="Top Cidades" series={topListToSeries(metrics.topCities, 'city')} darkMode={darkMode} accent={KPI.tertiary} />
        <RankingCard title="Top Equipamentos" series={topListToSeries(metrics.topEquipments, 'serialnumber')} darkMode={darkMode} accent={KPI.quaternary} />
      </Row>
    </>
  );
};

const MeasurementsDashboard = ({ darkMode, onLoadingChange }) => {
  const [data, setData] = useState(EMPTY_EXECUTIVE_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sourceTab, setSourceTab] = useState('dry');
  const styles = getDashboardShellStyles(darkMode);

  useEffect(() => {
    if (onLoadingChange) onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  const fetchDashboard = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');

    try {
      const params = refresh ? { refresh: 'true' } : {};
      const { data: response } = await Api.get('/api/v1/dashboard/executive', { params });
      setData(normalizeExecutiveDashboard(response));
    } catch (err) {
      setData(EMPTY_EXECUTIVE_DASHBOARD);
      setError('Não foi possível carregar o dashboard executivo de medidas.');
      console.log('Erro ao buscar dashboard executivo:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard(false);
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5" style={styles.panel}>
        <Spinner animation="border" style={{ color: '#8fad15' }} />
        <span className="ms-2" style={styles.mutedText}>Isso está demorando... (O dashboard de medidas é um pouco pesado)</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mb-0">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>{error}</span>
          <Button size="sm" variant="outline-danger" onClick={() => fetchDashboard(false)}>
            <MdRefresh className="me-1" /> Tentar novamente
          </Button>
        </div>
      </Alert>
    );
  }

  const hasAnyData = data.dry.totals.totalMeasures > 0 || data.bench.totals.totalMeasures > 0;

  if (!hasAnyData) {
    return (
      <div style={{ ...styles.panel, padding: '28px' }}>
        <h4 className="fw-bold mb-2" style={styles.titleText}>Nenhuma medida encontrada</h4>
        <p className="mb-3" style={styles.mutedText}>A API retornou totais zerados para silo e bancada.</p>
        <Button variant="outline-success" onClick={() => fetchDashboard(true)}>
          <MdRefresh className="me-1" /> Atualizar agora
        </Button>
      </div>
    );
  }

  const activeMetrics = sourceTab === 'bench' ? data.bench : data.dry;

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4" style={{ ...styles.panel, padding: '20px' }}>
        <div>
          <Badge className="mb-2" style={{ background: '#8fad15', color: '#fff' }}>Painel Executivo</Badge>
          <h3 className="fw-bold mb-1" style={styles.titleText}>Dashboard de Medidas</h3>
          <p className="mb-0" style={styles.mutedText}>
            Métricas consolidadas de silo (dry) e bancada (bench).
          </p>
        </div>
        <div className="d-flex flex-column align-items-start align-items-md-end gap-2">
          <Badge className="badge-light" pill>
            Atualizado: {formatGeneratedAt(data.generatedAt)}
          </Badge>
          {data.cached && (
            <small style={styles.mutedText}>Cache: {formatNumber(data.cacheAgeSeconds)}s</small>
          )}
          <Button variant="outline-success" size="sm" onClick={() => fetchDashboard(true)}>
            <MdRefresh className="me-1" /> Atualizar agora
          </Button>
        </div>
      </div>

      <Tabs
        activeKey={sourceTab}
        onSelect={(key) => setSourceTab(key)}
        className={`mb-4 painel-executivo-tabs ${darkMode ? 'painel-executivo-tabs--pills' : ''}`}
        variant={darkMode ? 'pills' : 'tabs'}
        style={getDashboardTabsStyle(darkMode)}
      >
        <Tab eventKey="dry" title="Silo (Dry)" />
        <Tab eventKey="bench" title="Bancada (Bench)" />
      </Tabs>

      <MetricsSection
        title={sourceTab === 'bench' ? 'Bancada (Bench)' : 'Silo (Dry)'}
        badge={sourceTab === 'bench' ? 'Bench' : 'Dry'}
        metrics={activeMetrics}
        topsPeriod={data.topsPeriod}
        darkMode={darkMode}
        styles={styles}
      />
    </>
  );
};

export default MeasurementsDashboard;
